/**
 * TrendChart — Overview activity trend (IA §2.2.2).
 *
 * Dual-axis Recharts `ComposedChart` with:
 *   - a muted bar series for `requests` (left Y axis)
 *   - an accent line series for `activeUsers` (right Y axis)
 *   - granularity-aware X axis tick formatter
 *
 * Bins are padded with `fillBins()` so a gap of zero-activity in the middle
 * of the range still reads as a continuous chart instead of two disjoint
 * segments. Empty data renders a centred message instead of a blank chart
 * (per IA §1.5 no-data state).
 *
 * The component stays presentational — data fetching lives in the caller
 * (Overview page) via TanStack Query. `loading=true` swaps in a fixed-height
 * skeleton so the page layout doesn't shift when data arrives.
 */

import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  TOOLTIP_CLASSNAME,
  TOOLTIP_VALUE_CLASSNAME,
  useChartTooltipTrigger,
} from '@/components/charts/ChartTooltip';
import {
  TimelineTotalsBadge,
  type TimelineTotalItem,
} from '@/components/charts/TimelineTotalsBadge';
// v1.4 dashboard-ux-fix: share the centralised bucket formatter with the rest
// of the timeline charts (Rating trend, Time saved) so hourly x-axes read as
// "Mon d HH:mm" instead of the ambiguous bare "HH:mm" this file carried.
import { chartPalette } from '@/lib/chart-palette';
import { fillBins, type TrendPoint, type TrendGranularity } from '@/lib/fill-bins';
import type { DayFilterMode } from '../../../../../services/analytics/day-filter-vocabulary.js';
import { formatBucketLabel } from '@/lib/format-bucket';

// `fillBins`, `TrendPoint` and `TrendGranularity` live in `@/lib/fill-bins` — a module that
// imports no React, so `tests/dashboard/trend-chart-day-filter.test.ts` can drive the padder
// with the runner that actually runs in this repository. Re-exported here because every
// caller (and `TrendChart.test.tsx`) imports them from this file.
export { fillBins } from '@/lib/fill-bins';
export type { TrendGranularity, TrendPoint } from '@/lib/fill-bins';

export interface TrendChartProps {
  data: TrendPoint[];
  loading?: boolean;
  /** Pixel height of the responsive container. Default 320 per IA §2.2.2. */
  height?: number;
  granularity: TrendGranularity;
  /**
   * Optional human-readable title rendered above the chart (bug F1). Matches
   * the layout TimeSavedChart uses — if absent, the title row is skipped so
   * we don't regress callers that frame the chart inside their own card.
   */
  title?: string;
  /**
   * WHICH DAYS THE SERIES IS ABOUT — the same three-option vocabulary the WHERE clause
   * behind the data reads (`DAY_FILTER_MODES`), passed through to `fillBins` so the chart
   * only synthesizes zero bars on days the backend would have returned.
   *
   * ⚠️ THIS USED TO BE `includeWeekends?: boolean` AND THE BOOLEAN IS THE DEFECT. The
   * filter bar has three states and `filters-schema.ts` collapses them with
   * `includeWeekends = dayFilter === 'all'`, so 'weekdays' and 'weekends' both arrived as
   * `false`. Under "Weekends only" the padder dropped every Sat/Sun — i.e. every bucket the
   * server returned — and drew five synthetic weekday zeros instead, while the totals badge
   * on the same header summed the unpadded series and printed the real figure. Defaults to
   * `'all'`, which pads every day, so a caller that does not thread the filter is unchanged.
   */
  dayFilter?: DayFilterMode;
  /**
   * v1.4 dashboard-ux-fix: optional per-series totals rendered to the right
   * of the title. Each entry matches a recharts series; pass `value: null`
   * for averages/percentages that can't be summed. Shown only when a title
   * is supplied (the totals need a header row to anchor to).
   */
  totals?: TimelineTotalItem[];
  /**
   * Optional free-form node rendered in the top-right of the title row,
   * right-adjacent to (or replacing) the `totals` badge. Used by Overview's
   * rated-split variant to render the "Total requests: X · Rated Y (N%)"
   * summary that isn't expressible as TimelineTotalItem[] (the text
   * interleaves two numbers + a share). Kept as ReactNode so callers can
   * theme it without widening TimelineTotalsBadge's schema.
   */
  badge?: React.ReactNode;
}

/**
 * Custom tooltip for the Overview activity trend (v1.4 dashboard-ux-fix).
 *
 * Users asked for "Total requests (тотал), количество рейтнутых" instead of
 * the raw `rated` / `notRated` / `activeUsers` rows the default ChartTooltip
 * painted. The default can't summarise because `showTotal` sums *every*
 * numeric payload entry including `activeUsers` — a different axis. A chart-
 * local renderer reads the payload by dataKey, derives total + rated-share,
 * and omits the redundant `notRated` row (it's Total − Rated by construction).
 *
 * Uses the shared tooltip class tokens so the surface stays in visual lock-
 * step with ChartTooltip across the rest of the dashboard.
 */
interface TrendTooltipEntry {
  dataKey?: string | number;
  value?: number | string | null;
  color?: string;
}

function TrendChartTooltip({
  active,
  payload,
  label,
  granularity,
}: {
  active?: boolean;
  payload?: TrendTooltipEntry[];
  label?: string | number;
  granularity: TrendGranularity;
}): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;

  const findValue = (key: string): number | null => {
    const entry = payload.find((p) => p.dataKey === key);
    const v = entry?.value;
    return typeof v === 'number' && Number.isFinite(v) ? v : null;
  };
  const findColor = (key: string): string | undefined =>
    payload.find((p) => p.dataKey === key)?.color;

  const rmRequests = findValue('rmRequests');
  const nonRmRequests = findValue('nonRmRequests');
  const requestsLegacy = findValue('requests');
  const activeUsers = findValue('activeUsers');

  const hasSplit = rmRequests !== null || nonRmRequests !== null;
  const total = hasSplit
    ? (rmRequests ?? 0) + (nonRmRequests ?? 0)
    : (requestsLegacy ?? 0);

  // Series colours come from the <Bar>/<Line> fill/stroke props recharts
  // passes into each payload entry. Fall back to the default palette so the
  // swatches don't vanish if recharts omits `color` on a given entry.
  const rmSwatch = findColor('rmRequests') ?? chartPalette.rmRequests;
  const nonRmSwatch = findColor('nonRmRequests') ?? chartPalette.nonRmRequests;
  const activeUsersSwatch = findColor('activeUsers') ?? chartPalette.activeUsers;

  const bucketLabel =
    typeof label === 'string' ? formatBucketLabel(label, granularity) : String(label ?? '');

  return (
    <div data-testid="chart-tooltip" className={TOOLTIP_CLASSNAME}>
      {bucketLabel && <div className="mb-1 font-medium">{bucketLabel}</div>}
      <ul className="space-y-0.5">
        <li className="flex items-center gap-2">
          <span
            aria-hidden
            className="inline-block h-2 w-2 rounded-sm"
            style={{ background: hasSplit ? nonRmSwatch : rmSwatch }}
          />
          <span className={TOOLTIP_VALUE_CLASSNAME}>
            Total requests: <span className="font-medium">{total.toLocaleString()}</span>
          </span>
        </li>
        {hasSplit && rmRequests !== null && (
          <li className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: rmSwatch }}
            />
            <span className={TOOLTIP_VALUE_CLASSNAME}>
              RM: <span className="font-medium">{rmRequests.toLocaleString()}</span>
            </span>
          </li>
        )}
        {hasSplit && nonRmRequests !== null && (
          <li className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: nonRmSwatch }}
            />
            <span className={TOOLTIP_VALUE_CLASSNAME}>
              Non-RM: <span className="font-medium">{nonRmRequests.toLocaleString()}</span>
            </span>
          </li>
        )}
        {activeUsers !== null && (
          <li className="flex items-center gap-2">
            <span
              aria-hidden
              className="inline-block h-2 w-2 rounded-sm"
              style={{ background: activeUsersSwatch }}
            />
            <span className={TOOLTIP_VALUE_CLASSNAME}>
              Active users: <span className="font-medium">{activeUsers.toLocaleString()}</span>
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}

/**
 * Format a bucket label per the active granularity.
 *
 * v1.4 dashboard-ux-fix: delegates to the shared `formatBucketLabel` helper
 * in `@/lib/format-bucket` so every timeline chart (Overview activity-trend,
 * Quality rating-trend, Overview time-saved) agrees on the axis format. The
 * previous local implementation returned a bare "HH:mm" on hourly buckets,
 * which made a 24h window ambiguous — two days of hourly samples read as
 * the same tick sequence. The shared helper renders "Mon d HH:mm" on
 * hourly so the day boundary is visible.
 *
 * The internal `TrendGranularity` is a strict subset of the shared helper's
 * `BucketGranularity` ('hourly' | 'daily' | 'weekly' are all accepted), so
 * the call is a safe pass-through.
 */
function formatBucket(bucket: string, g: TrendGranularity): string {
  return formatBucketLabel(bucket, g);
}

export function TrendChart({
  data,
  loading = false,
  height = 320,
  granularity,
  title,
  dayFilter = 'all',
  totals,
  badge,
}: TrendChartProps): React.ReactElement {
  // Mobile tap persistence — on `(hover: none)` pointers the tooltip switches
  // from hover to click trigger so a tap keeps it open instead of flashing.
  const tooltipTrigger = useChartTooltipTrigger();
  // Title wrapper (bug F1) — matches TimeSavedChart's header layout. Rendered
  // only when a title is supplied so framed-in-card callers don't double up.
  //
  // v1.4 dashboard-ux-fix: the title row now grows to a flex row so the
  // optional `totals` badge (right side) can sit alongside the title (left).
  // On narrow viewports the two wrap via `flex-wrap` so the numbers never
  // clip off-screen.
  const withTitle = (node: React.ReactNode): React.ReactElement => {
    if (!title) return <>{node}</>;
    return (
      <div data-testid="trend-chart-wrapper" className="w-full space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm font-medium text-muted-foreground">
          <span>{title}</span>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            {totals && totals.length > 0 && (
              <TimelineTotalsBadge
                data-testid="trend-chart-totals"
                /* The badge's em-dash means "cannot be summed", so a caller
                   that ever uses `totals` must not inherit it for "not counted
                   yet" — this chart's own loading branch returns above, but a
                   totals row rendered from a half-landed page would. */
                loading={loading}
                items={totals}
              />
            )}
            {badge && (
              <span
                data-testid="trend-chart-badge"
                className="text-xs tabular-nums text-muted-foreground"
              >
                {badge}
              </span>
            )}
          </div>
        </div>
        {node}
      </div>
    );
  };

  if (loading) {
    return withTitle(
      <div
        data-testid="trend-chart-skeleton"
        className="w-full rounded bg-muted animate-pulse"
        style={{ height }}
      />,
    );
  }

  if (!data || data.length === 0) {
    return withTitle(
      <div
        data-testid="trend-chart-empty"
        className="flex items-center justify-center w-full text-sm text-muted-foreground"
        style={{ height }}
      >
        No activity in selected period
      </div>,
    );
  }

  const filled = fillBins(data, granularity, dayFilter);
  // Overview v1.4.1: swap the rated/notRated stack for an RM/Non-RM stack so
  // the chart shares the TimeSavedChart vocabulary. When the server hands
  // back `rmRequests` + `nonRmRequests`, we stack them; otherwise we fall
  // back to a single muted "Requests" bar.
  const splitByRole = filled.some(
    (d) => typeof d.rmRequests === 'number' || typeof d.nonRmRequests === 'number',
  );
  const stacked = splitByRole
    ? filled.map((d) => {
        const rmRequests = typeof d.rmRequests === 'number' ? d.rmRequests : 0;
        const nonRmRequests =
          typeof d.nonRmRequests === 'number' ? d.nonRmRequests : 0;
        return { ...d, rmRequests, nonRmRequests };
      })
    : filled;

  return withTitle(
    <div data-testid="trend-chart" style={{ width: '100%', height }}>
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={stacked} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="bucket"
            tickFormatter={(v: string) => formatBucket(v, granularity)}
          />
          <YAxis yAxisId="left" orientation="left" />
          <YAxis yAxisId="right" orientation="right" />
          {/* bug F1 — themed tooltip replaces recharts' default white panel.
              Mobile-tap persistence (flash-on-touch fix) via trigger switch. */}
          {/* bug F1 — themed tooltip replaces recharts' default white panel.
              v1.4 dashboard-ux-fix: TrendChartTooltip summarises the bar stack
              into "Total requests / Rated (N%) / Active users" instead of the
              default row-per-series dump, which hid the total behind two
              half-numbers and repeated the redundant `notRated` value. */}
          <Tooltip
            {...tooltipTrigger}
            cursor={false}
            content={<TrendChartTooltip granularity={granularity} />}
          />
          <Legend />
          {/* Recharts walks its direct children with React.Children.map and
              identifies Bar/Line/etc by their component type. Wrapping the
              two stacked bars in a Fragment hides them from that walk — the
              chart renders with zero Bar series. Keep every <Bar> as a
              direct child of ComposedChart (no Fragment, no array) and use
              separate `splitByRole && ...` guards instead. */}
          {/* No activeBar / hover stroke on this chart: it's a read-only
              timeline (no drill-down on Overview), so the hover outline
              would falsely advertise clickability. Same rule TimeSavedChart
              follows — `activeBar` is only wired up when the chart exposes
              a drill-down callback. */}
          {splitByRole && (
            <Bar
              yAxisId="left"
              dataKey="rmRequests"
              stackId="requests"
              fill={chartPalette.rmRequests}
              name="RM"
              activeBar={false}
              isAnimationActive={false}
            />
          )}
          {splitByRole && (
            <Bar
              yAxisId="left"
              dataKey="nonRmRequests"
              stackId="requests"
              fill={chartPalette.nonRmRequests}
              name="Non-RM"
              activeBar={false}
              isAnimationActive={false}
            />
          )}
          {!splitByRole && (
            <Bar
              yAxisId="left"
              dataKey="requests"
              fill={chartPalette.nonRmRequests}
              name="Requests"
              activeBar={false}
              isAnimationActive={false}
            />
          )}
          {/* Active users LINE uses cyan (chartPalette.activeUsers), NOT the
              RM blue — Bug A2: when RM bars and Active-users line share the
              same blue, the line disappears against the RM stack. Cyan is
              a distinct companion colour that still reads as a "cool /
              neutral" signal so it doesn't compete with green wins or red
              alerts elsewhere on the page. */}
          <Line
            yAxisId="right"
            dataKey="activeUsers"
            type="monotone"
            stroke={chartPalette.activeUsers}
            strokeWidth={2}
            name="Active users"
            dot={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>,
  );
}
